import ReactGA from "react-ga4";

export type AnalyticsPlatform = "web" | "mobile_web" | "native_app";

declare global {
  interface Window {
    __SMARTFARM_PLATFORM?: AnalyticsPlatform;
  }
}

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();

const ROUTE_FEATURES: Array<{match: RegExp; feature: string}> = [
  {match: /^\/$/, feature: "public_landing"},
  {match: /^\/login$/, feature: "login"},
  {match: /^\/request-access$/, feature: "request_access"},
  {match: /^\/forgot-password$/, feature: "forgot_password"},
  {match: /^\/reset-password$/, feature: "reset_password"},
  {match: /^\/onboarding$/, feature: "onboarding"},
  {match: /^\/dashboard$/, feature: "dashboard"},
  {match: /^\/farms$/, feature: "farms"},
  {match: /^\/ponds$/, feature: "ponds"},
  {match: /^\/ponds\/[^/]+$/, feature: "pond_detail"},
  {match: /^\/predictive$/, feature: "predictive_analytics"},
  {match: /^\/advanced-predictive$/, feature: "advanced_predictive"},
  {match: /^\/market-prices$/, feature: "market_prices"},
  {match: /^\/seed-availability$/, feature: "seed_availability"},
  {match: /^\/help$/, feature: "help"},
  {match: /^\/template-management$/, feature: "template_management"},
  {match: /^\/tenants$/, feature: "tenants"},
  {match: /^\/access-requests$/, feature: "access_requests"},
  {match: /^\/users$/, feature: "users"},
  {match: /^\/config$/, feature: "configuration"},
];

let initialized = false;

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 900px)").matches;
}

export function getAnalyticsPlatform(): AnalyticsPlatform {
  if (typeof window !== "undefined" && window.__SMARTFARM_PLATFORM === "native_app") {
    return "native_app";
  }
  return isMobileViewport() ? "mobile_web" : "web";
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}

export function initAnalytics(): void {
  if (initialized || !MEASUREMENT_ID) {
    if (!MEASUREMENT_ID && process.env.NODE_ENV !== "production") {
      console.info("[analytics] REACT_APP_GA_MEASUREMENT_ID is not set; analytics disabled");
    }
    return;
  }

  ReactGA.initialize(MEASUREMENT_ID, {
    gaOptions: {
      anonymizeIp: true,
    },
  });
  initialized = true;
}

export function setAnalyticsUserId(userId?: string | null): void {
  if (!initialized || !userId) return;
  ReactGA.set({user_id: userId});
}

export function clearAnalyticsUserId(): void {
  if (!initialized) return;
  ReactGA.set({user_id: undefined});
}

export function trackEvent(name: string, params: Record<string, string | number | boolean | undefined> = {}): void {
  if (!initialized) return;

  const platform = getAnalyticsPlatform();
  const cleaned: Record<string, string | number | boolean> = {platform};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  });

  ReactGA.event(name, cleaned);
}

export function featureFromPath(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const hit = ROUTE_FEATURES.find(({match}) => match.test(normalized));
  return hit?.feature || "unknown";
}

export function trackPageView(pathname: string, search = ""): void {
  if (!initialized) return;

  const page = `${pathname}${search}`;
  const feature = featureFromPath(pathname);
  const platform = getAnalyticsPlatform();

  ReactGA.send({hitType: "pageview", page, title: feature});
  trackEvent("feature_access", {
    feature_name: feature,
    page_path: page,
    platform,
  });
}

export function trackLogin(method: "email" | "google", userId?: string): void {
  if (userId) {
    setAnalyticsUserId(userId);
  }
  trackEvent("login", {method});
}

export function trackLogout(): void {
  trackEvent("logout");
  clearAnalyticsUserId();
}

export function trackFeatureAccess(featureName: string, extra: Record<string, string | number | boolean | undefined> = {}): void {
  trackEvent("feature_access", {
    feature_name: featureName,
    ...extra,
  });
}
